import type * as THREE from "three";
import { getCeilingVisibilityGroup } from "../../engines/ceilingVisibility";
import { applyDividerVisibility, type FrontVisibilityGroups } from "../../engines/frontVisibility";
import type { BuildResult, VisibilityFlags } from "../../engines/types";

/** Divider cutaways belong to the current view, never the saved layer choices. */
export type SceneVisibility = VisibilityFlags & { dividerWall?: boolean };

export type SceneGroupRefs = {
	foundation?: THREE.Group;
	deck?: THREE.Group;
	framing?: THREE.Group;
	panel?: THREE.Group;
	roof?: THREE.Group;
	interior?: THREE.Group;
	exterior?: THREE.Group;
};

export function applyFrontVisibility(
	frontGroups: FrontVisibilityGroups,
	frontVisible: boolean,
): void {
	for (const group of Object.values(frontGroups)) {
		if (group) {
			group.visible = frontVisible;
		}
	}
}

/**
 * The bastupanel + taklist in the innertak: owned by the PANEL layer, but they
 * are also the ceiling, so TAK off must take them down too.
 *
 * Only the panel-group half of the ceiling needs this traversal. The hidden
 * stack behind it (isolering, folie, takgles) is a whole subgroup of the
 * interior group — see `applyCeilingStackVisibility`. The taklist cannot ride
 * a subgroup like that: its front run must obey `panel && roof && frontWall`,
 * and a group chain only ANDs two flags for a mesh that already sits inside
 * the front-visibility subgroup — so `roof` goes on the mesh itself and the
 * two group levels above it supply the other flags.
 */
export function isCeilingFinishMesh(node: THREE.Object3D): boolean {
	const name = node.name;
	return (
		name.startsWith("detail/ceiling-panel/") ||
		name.startsWith("detail/ceiling-trim/")
	);
}

export function applyCeilingFinishVisibility(
	panelGroup: THREE.Group | undefined,
	visibility: Pick<VisibilityFlags, "panel" | "roof">,
): void {
	if (!panelGroup) return;
	panelGroup.traverse((node) => {
		if (!isCeilingFinishMesh(node)) return;
		node.visible = visibility.roof && visibility.panel;
	});
}

/**
 * The ceiling's hidden layer stack rides a named subgroup of the interior
 * group, so `roof` is applied to that subgroup and the parent group's
 * `interior` flag supplies the `&&` — no per-mesh bookkeeping, and a rebuilt
 * interior subsystem brings a fresh subgroup that this finds by name.
 */
export function applyCeilingStackVisibility(
	interiorGroup: THREE.Group | undefined,
	visibility: Pick<VisibilityFlags, "roof">,
): void {
	if (!interiorGroup) return;
	const ceilingStack = getCeilingVisibilityGroup(interiorGroup);
	if (!ceilingStack) return;
	ceilingStack.visible = visibility.roof;
}

export function applyBuildResultVisibility(
	result: BuildResult,
	visibility: SceneVisibility,
): void {
	result.foundationGroup.visible = visibility.foundation;
	result.deckGroup.visible = visibility.deck;
	result.framingGroup.visible = visibility.framing;
	result.panelGroup.visible = visibility.panel;
	result.roofGroup.visible = visibility.roof;
	result.interiorGroup.visible = visibility.interior;
	result.exteriorGroup.visible = visibility.exterior;
	applyFrontVisibility(result.frontVisibilityGroups, visibility.frontWall);
	applyDividerVisibility(result.framingGroup, visibility.dividerWall !== false);
	applyDividerVisibility(result.interiorGroup, visibility.dividerWall !== false);
	applyCeilingFinishVisibility(result.panelGroup, visibility);
	applyCeilingStackVisibility(result.interiorGroup, visibility);
}

export function applySceneGroupVisibility(
	groups: SceneGroupRefs,
	frontGroups: FrontVisibilityGroups,
	visibility: SceneVisibility,
): void {
	if (groups.foundation) groups.foundation.visible = visibility.foundation;
	if (groups.deck) groups.deck.visible = visibility.deck;
	if (groups.framing) groups.framing.visible = visibility.framing;
	if (groups.panel) groups.panel.visible = visibility.panel;
	if (groups.roof) groups.roof.visible = visibility.roof;
	if (groups.interior) groups.interior.visible = visibility.interior;
	if (groups.exterior) groups.exterior.visible = visibility.exterior;
	applyFrontVisibility(frontGroups, visibility.frontWall);
	applyDividerVisibility(groups.framing, visibility.dividerWall !== false);
	applyDividerVisibility(groups.interior, visibility.dividerWall !== false);
	applyCeilingFinishVisibility(groups.panel, visibility);
	applyCeilingStackVisibility(groups.interior, visibility);
}
